import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllSlideshows,
  createSlideshow,
  updateSlideshow,
  deleteSlideshow,
  subscribeToAllSlideshows
} from '../services/slideshows';
import { getAllCompanies } from '../services/companies';
import './SlideshowManagement.css';

function SlideshowManagement() {
  const navigate = useNavigate();
  const [slideshows, setSlideshows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSlideshow, setEditingSlideshow] = useState(null);
  const [filterType, setFilterType] = useState('all'); // all, company, global

  // Form state
  const [formData, setFormData] = useState({
    companyId: '',
    title: '',
    mediaUrl: '',
    mediaType: 'image',
    duration: 10,
    isAd: false,
    isDisabled: false,
    order: 0
  });

  useEffect(() => {
    loadData();

    // Subscribe to real-time slideshow updates
    const unsubscribe = subscribeToAllSlideshows((slideshowsData) => {
      setSlideshows(slideshowsData);
    });

    return () => unsubscribe();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [slideshowsData, companiesData] = await Promise.all([
        getAllSlideshows(),
        getAllCompanies()
      ]);

      setSlideshows(slideshowsData);
      setCompanies(companiesData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingSlideshow) {
        // Update existing slideshow
        await updateSlideshow(editingSlideshow.id, {
          title: formData.title,
          mediaUrl: formData.mediaUrl,
          mediaType: formData.mediaType,
          duration: parseInt(formData.duration),
          isAd: formData.isAd,
          isDisabled: formData.isDisabled,
          order: parseInt(formData.order)
        });
        alert('輪播更新成功');
      } else {
        // Create new slideshow
        await createSlideshow({
          companyId: formData.companyId || null,
          title: formData.title,
          mediaUrl: formData.mediaUrl,
          mediaType: formData.mediaType,
          duration: parseInt(formData.duration),
          isAd: formData.isAd,
          isDisabled: formData.isDisabled,
          order: parseInt(formData.order)
        });
        alert('輪播建立成功');
      }

      // Reset form
      setShowCreateModal(false);
      setEditingSlideshow(null);
      setFormData({
        companyId: '',
        title: '',
        mediaUrl: '',
        mediaType: 'image',
        duration: 10,
        isAd: false,
        isDisabled: false,
        order: 0
      });
    } catch (error) {
      console.error('Error saving slideshow:', error);
      alert('操作失敗: ' + error.message);
    }
  };

  const handleEdit = (slideshow) => {
    setEditingSlideshow(slideshow);
    setFormData({
      companyId: slideshow.companyId || '',
      title: slideshow.title || '',
      mediaUrl: slideshow.mediaUrl,
      mediaType: slideshow.mediaType,
      duration: slideshow.duration,
      isAd: slideshow.isAd,
      isDisabled: slideshow.isDisabled,
      order: slideshow.order
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (slideshowId) => {
    if (!window.confirm('確定要刪除此輪播嗎？')) {
      return;
    }

    try {
      await deleteSlideshow(slideshowId);
      alert('輪播刪除成功');
    } catch (error) {
      console.error('Error deleting slideshow:', error);
      alert('刪除失敗: ' + error.message);
    }
  };

  const handleCancel = () => {
    setShowCreateModal(false);
    setEditingSlideshow(null);
    setFormData({
      companyId: '',
      title: '',
      mediaUrl: '',
      mediaType: 'image',
      duration: 10,
      isAd: false,
      isDisabled: false,
      order: 0
    });
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return '全域廣告';
    const company = companies.find(c => c.id === companyId);
    return company ? company.companyName : '未知公司';
  };

  const filteredSlideshows = slideshows.filter(s => {
    if (filterType === 'all') return true;
    if (filterType === 'global') return !s.companyId;
    if (filterType === 'company') return !!s.companyId;
    return true;
  });

  if (loading) {
    return (
      <div className="slideshow-management">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="slideshow-management">
      <div className="header">
        <h1>輪播管理</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            ← 返回
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            ➕ 新增輪播
          </button>
        </div>
      </div>

      <div className="filters">
        <label>篩選類型:</label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="filter-select"
        >
          <option value="all">全部輪播</option>
          <option value="company">公司專屬</option>
          <option value="global">全域廣告</option>
        </select>
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingSlideshow ? '編輯輪播' : '新增輪播'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>所屬公司</label>
                <select
                  value={formData.companyId}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                >
                  <option value="">全域廣告（所有公司）</option>
                  {companies.filter(c => c.isActive).map(company => (
                    <option key={company.id} value={company.id}>
                      {company.companyName} ({company.companyCode})
                    </option>
                  ))}
                </select>
                <small className="help-text">選擇公司或留空為全域廣告</small>
              </div>

              <div className="form-group">
                <label>標題</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="輪播標題（選填）"
                />
              </div>

              <div className="form-group">
                <label>媒體類型 *</label>
                <select
                  value={formData.mediaType}
                  onChange={(e) => setFormData({ ...formData, mediaType: e.target.value })}
                  required
                >
                  <option value="image">圖片</option>
                  <option value="video">影片</option>
                </select>
              </div>

              <div className="form-group">
                <label>媒體 URL *</label>
                <input
                  type="url"
                  value={formData.mediaUrl}
                  onChange={(e) => setFormData({ ...formData, mediaUrl: e.target.value })}
                  required
                  placeholder="https://example.com/image.jpg"
                />
                <small className="help-text">
                  圖片格式: JPG, PNG, GIF | 影片格式: MP4, WebM
                </small>
              </div>

              {formData.mediaUrl && (
                <div className="form-group">
                  <label>預覽</label>
                  {formData.mediaType === 'image' ? (
                    <img
                      src={formData.mediaUrl}
                      alt="預覽"
                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                      onError={(e) => { e.target.src = ''; e.target.alt = '無法載入圖片'; }}
                    />
                  ) : (
                    <video
                      src={formData.mediaUrl}
                      controls
                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                    >
                      您的瀏覽器不支援影片播放
                    </video>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>播放時長（秒） *</label>
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  required
                  min="1"
                  max="300"
                />
                <small className="help-text">1-300 秒</small>
              </div>

              <div className="form-group">
                <label>排序</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  min="0"
                />
                <small className="help-text">數字越小越優先顯示</small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isAd}
                    onChange={(e) => setFormData({ ...formData, isAd: e.target.checked })}
                  />
                  標記為廣告
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isDisabled}
                    onChange={(e) => setFormData({ ...formData, isDisabled: e.target.checked })}
                  />
                  停用此輪播
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSlideshow ? '更新' : '建立'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="slideshows-list">
        {filteredSlideshows.length === 0 ? (
          <div className="empty-state">
            <p>尚未建立任何輪播</p>
            <p className="help-text">點擊上方「新增輪播」按鈕開始建立</p>
          </div>
        ) : (
          <div className="slideshows-grid">
            {filteredSlideshows.map((slideshow) => (
              <div key={slideshow.id} className="slideshow-card">
                <div className="slideshow-preview">
                  {slideshow.mediaType === 'image' ? (
                    <img
                      src={slideshow.mediaUrl}
                      alt={slideshow.title || '輪播圖片'}
                      onError={(e) => { e.target.src = ''; e.target.alt = '無法載入'; }}
                    />
                  ) : (
                    <video src={slideshow.mediaUrl} muted>
                      您的瀏覽器不支援影片
                    </video>
                  )}
                  {slideshow.isDisabled && (
                    <div className="disabled-overlay">已停用</div>
                  )}
                </div>
                <div className="slideshow-info">
                  <h3>{slideshow.title || '（無標題）'}</h3>
                  <div className="info-row">
                    <span className="label">類型:</span>
                    <span className="value">
                      {getCompanyName(slideshow.companyId)}
                      {slideshow.isAd && <span className="ad-badge">廣告</span>}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">媒體:</span>
                    <span className="value">{slideshow.mediaType === 'image' ? '圖片' : '影片'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">時長:</span>
                    <span className="value">{slideshow.duration} 秒</span>
                  </div>
                  <div className="info-row">
                    <span className="label">排序:</span>
                    <span className="value">{slideshow.order}</span>
                  </div>
                </div>
                <div className="slideshow-actions">
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => handleEdit(slideshow)}
                  >
                    編輯
                  </button>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDelete(slideshow.id)}
                  >
                    刪除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SlideshowManagement;
